/**
 * Direct-to-R2 single file upload helper.
 *
 * Uploads `file` straight to Cloudflare R2 using a short-lived presigned PUT
 * URL the backend hands out. Bypasses the FastAPI pod entirely so big and/or
 * bulk image uploads don't hit the Cloudflare/Kubernetes ingress body-size
 * limit (~100MB total per request) or saturate backend memory.
 *
 * Use this for ALL image uploads going forward (replaces the older
 * `POST /api/upload` multipart path which sends the bytes through the pod).
 *
 * @param {import('axios').AxiosInstance} api  axios instance with auth header
 * @param {File} file                          the browser File
 * @param {(pct: number) => void} [onProgress] called with 0..100 per file
 * @returns {Promise<string>}                  the final `/api/uploads/...` URL
 */
export async function uploadFileDirectToR2(api, file, onProgress) {
    const init = await api.post("/upload/direct/init", {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
    });
    const { upload_url, content_type, final_url } = init.data || {};
    if (!upload_url || !final_url) {
        throw new Error("Direct-upload init mislukt (server gaf geen URL)");
    }

    // XHR (not fetch) is needed because `fetch` exposes no upload progress.
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", upload_url);
        xhr.setRequestHeader("Content-Type", content_type || file.type || "application/octet-stream");
        if (onProgress) {
            xhr.upload.addEventListener("progress", (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            });
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve(null);
            else reject(new Error(`R2-upload mislukt (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Netwerkfout tijdens upload"));
        xhr.send(file);
    });

    return final_url;
}
